import React, { useState, useEffect } from 'react';
import { Box, Button, IconButton, Avatar, Menu, MenuItem, ListItemIcon, ListItemText, Badge, Tooltip, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import NotificationsIcon from '@mui/icons-material/Notifications';
import PersonIcon from '@mui/icons-material/Person';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import LogoutIcon from '@mui/icons-material/Logout';
import SwitchIcon from '../../icons/SwitchIcon';
import ApiService from '../../../services/api';
import logo from '../../../assets/VisibeenLogo.png';

import logger from '../../../utils/logger';
const HeaderContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '82px',
  backgroundColor: '#0b91d6',
  boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.06)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '0 66px 0 66px'
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center'
}));

const LogoImage = styled('img')(({ theme }) => ({
  height: '40px',
  width: 'auto',
  objectFit: 'contain',
  cursor: 'pointer',
  transition: 'opacity 0.2s ease',
  '&:hover': {
    opacity: 0.8
  }
}));

const SwitchDashboardButton = styled(Button)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.20)',
  borderRadius: '8px',
  padding: '12px 16px',
  color: '#ffffff',
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 500,
  textTransform: 'none',
  gap: '12px',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.30)'
  }
}));

const AdminDashboardHeader = () => {
  const navigate = useNavigate();
  const [anchorElUser, setAnchorElUser] = useState(null);
  const [displayName, setDisplayName] = useState('Admin');
  const [avatarLetter, setAvatarLetter] = useState('A');

  useEffect(() => {
    const deriveName = (u) => {
      if (!u) return null;
      const full = u.full_name || [u.first_name, u.last_name].filter(Boolean).join(' ') || u.name || u.userdetails?.full_name || u.email;
      return typeof full === 'string' && full.trim() ? full.trim() : null;
    };
    const setFromName = (name) => {
      let initial = name && name.trim().charAt(0).toUpperCase();
      if (!initial && name && name.includes('@')) initial = name.charAt(0).toUpperCase();
      setDisplayName(name || 'Admin');
      setAvatarLetter(initial || 'A');
    };

    try {
      const keys = ['auth', 'user', 'admin', 'userdetails'];
      for (const k of keys) {
        const raw = localStorage.getItem(k);
        if (!raw) continue;
        let parsed = null;
        try { parsed = JSON.parse(raw); } catch { parsed = raw; }
        const cand = deriveName(parsed) || deriveName(parsed?.user) || deriveName(parsed?.data);
        if (cand) {
          setFromName(cand);
          return; // stop after first match
        }
      }
    } catch { }

    // Fallback to API
    (async () => {
      try {
        const res = await ApiService.get('/admin/auth/get-admin');
        const root = res?.data ?? res;
        const user = root?.data?.admin || root?.admin || root?.data || root;
        const cand = Array.isArray(user) ? user[0] : user;
        const name = deriveName(cand);
        if (name) {
          // cache to localStorage for faster next render
          try { localStorage.setItem('user', JSON.stringify(cand)); } catch { }
        }
        setFromName(name);
      } catch (e) {
        // keep defaults
        setFromName('Admin');
      }
    })();
  }, []);

  const handleSwitchDashboard = () => {
    logger.log('Switch dashboard clicked');
  };
  // const AddClient = () => {
  //   navigate('/admin-dashboard/add-client');
  // }

  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const goToProfile = () => {
    handleCloseUserMenu();
    navigate('/admin-dashboard/profile');
  };

  const goToChangePassword = () => {
    handleCloseUserMenu();
    navigate('/admin-dashboard/change-password');
  };

  const handleLogout = async () => {
    handleCloseUserMenu();
    try {
      // Derive email from stored auth if available
      let email = 'admin@visibeen.com';
      try {
        const authRaw = localStorage.getItem('auth');
        if (authRaw) {
          const auth = JSON.parse(authRaw);
          if (auth?.email) email = auth.email;
          if (auth?.user?.email) email = auth.user.email;
        }
      } catch { }

      // Call API to invalidate the session
      await ApiService.post('/admin/auth/logout', { email });
    } catch (e) {
      // Non-blocking: proceed with client-side logout even if API fails
      logger.warn('Logout API failed, proceeding with client-side logout', e);
    } finally {
      try {
        localStorage.removeItem('token');
        localStorage.removeItem('auth');

        // Clear all monthly plan caches on admin logout
        try {
          const keysToRemove = [];
          for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && (key.startsWith('monthly_plan_') || key.startsWith('weekly_products_services_'))) {
              keysToRemove.push(key);
            }
          }
          keysToRemove.forEach(key => localStorage.removeItem(key));
          console.log(`[Admin Logout] Cleared ${keysToRemove.length} monthly plan cache entries`);
        } catch (e) {
          console.warn('[Admin Logout] Failed to clear monthly plan caches:', e);
        }
      } catch { }
      navigate('/login');
    }
  };

  return (
    <HeaderContainer>
      <LogoContainer>
        <LogoImage
          src={logo}
          alt="VISIBEEN"
          onClick={() => navigate('/admin-dashboard')}
          title="Go to Dashboard"
        />
      </LogoContainer>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <SwitchDashboardButton onClick={handleSwitchDashboard}>
          <SwitchIcon width={16} height={14} color="#ffffff" />
          Switch Dashboard
        </SwitchDashboardButton>
        {/* <SwitchDashboardButton onClick={AddClient}>
          <span>Add Client</span>
        </SwitchDashboardButton> */}
        <Tooltip title="Notifications">
        </Tooltip>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Account settings">
            <IconButton onClick={handleOpenUserMenu} size="small" sx={{ p: 0 }} aria-controls={Boolean(anchorElUser) ? 'user-menu' : undefined} aria-haspopup="true" aria-expanded={Boolean(anchorElUser) ? 'true' : undefined}>
              <Avatar sx={{ width: 34, height: 34, bgcolor: '#121927' }}>{avatarLetter}</Avatar>
            </IconButton>
          </Tooltip>
          <Typography sx={{ color: '#ffffff', fontWeight: 600 }}>{displayName}</Typography>
        </Box>

        <Menu
          id="user-menu"
          anchorEl={anchorElUser}
          open={Boolean(anchorElUser)}
          onClose={handleCloseUserMenu}
          onClick={handleCloseUserMenu}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          PaperProps={{
            elevation: 3,
            sx: {
              mt: 1.5,
              overflow: 'visible',
              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.12))',
              borderRadius: '10px',
              minWidth: 220
            }
          }}
        >
          <MenuItem onClick={goToProfile}>
            <ListItemIcon>
              <PersonIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Profile" />
          </MenuItem>
          <MenuItem onClick={goToChangePassword}>
            <ListItemIcon>
              <VpnKeyIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Change Password" />
          </MenuItem>
          <MenuItem onClick={handleLogout}>
            <ListItemIcon>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Logout" />
          </MenuItem>
        </Menu>
      </Box>
    </HeaderContainer>
  );
};

export default AdminDashboardHeader;